import { motion } from "framer-motion";
import { Building2, LandPlot, Briefcase } from "lucide-react";

const AREAS = [
  {
    key: "commerciale",
    label: "01 — Diritto Commerciale",
    title: "Partner strategico per l'attività d'impresa.",
    image:
      "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85&auto=format",
    icon: Briefcase,
    items: [
      "Strutturazione dei processi interni",
      "Definizione di funzioni e deleghe",
      "Mappatura dei rischi legali & compliance",
      "Recupero del credito commerciale",
      "Contenzioso giudiziale & stragiudiziale",
      "Contrattualistica quotidiana d'impresa",
      "Ruolo di manager legale esterno",
    ],
  },
  {
    key: "corporate",
    label: "02 — Diritto Societario & M&A",
    title: "Assistenza legale a soci, imprese e operazioni straordinarie.",
    image:
      "https://images.unsplash.com/photo-1758691736493-aa6d22c0f8a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBib2FyZHJvb20lMjBtZWV0aW5nfGVufDB8fHx8MTc3ODg1NzI1Mnww&ixlib=rb-4.1.0&q=85",
    icon: Building2,
    items: [
      "Governance societaria",
      "Patti parasociali & rapporti tra soci",
      "Acquisizioni e cessioni",
      "Operazioni straordinarie · M&A",
      "Due diligence legali",
      "Fondi di private equity",
      "Riorganizzazioni & operazioni sul capitale",
    ],
  },
  {
    key: "realestate",
    label: "03 — Diritto Immobiliare",
    title: "Assistenza legale in operazioni immobiliari e sviluppo.",
    image:
      "https://images.unsplash.com/photo-1508450859948-4e04fabaa4ea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwcmVhbCUyMGVzdGF0ZSUyMGNvbnN0cnVjdGlvbnxlbnwwfHx8fDE3Nzg4NTcyNTJ8MA&ixlib=rb-4.1.0&q=85",
    icon: LandPlot,
    items: [
      "Investimenti immobiliari",
      "Sviluppo immobiliare",
      "Contratti general contractor · EPC",
      "Appalti e subappalti",
      "Operazioni immobiliari complesse",
      "Permute · Rent to buy",
      "Riqualificazione & valorizzazione",
    ],
  },
];

export default function PracticeAreas() {
  return (
    <section
      id="aree"
      className="relative py-24 lg:py-32 border-t border-[#FAF9F6]/10"
      data-testid="practice-areas-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-end justify-between mb-16 gap-6 flex-wrap">
          <div>
            <div className="overline mb-4">Aree di Attività</div>
            <h2 className="font-display text-4xl lg:text-6xl font-medium leading-[1.02] tracking-tight max-w-3xl">
              Tre aree di diritto.
              <span className="block text-[#FAF9F6]/50">
                Un partner strategico.
              </span>
            </h2>
            <p className="mt-6 max-w-xl text-[15px] text-[#FAF9F6]/60 leading-relaxed">
              Assistenza legale continuativa in tutte le fasi di vita
              dell&apos;azienda — dalla strutturazione operativa quotidiana
              alle operazioni più rilevanti — con la logica di un manager
              legale esterno integrato nel business.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {AREAS.map((area, i) => {
            const Icon = area.icon;
            return (
              <motion.article
                key={area.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className="bg-[#EAECE7] text-[#0A0A0A] border border-[#0A0A0A]/12 group hover:border-[#1B3B36]/50 transition-colors"
                data-testid={`area-card-${area.key}`}
              >
                <div className="relative h-64 lg:h-72 overflow-hidden">
                  <img
                    src={area.image}
                    alt={area.title}
                    className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/30 to-transparent" />
                  <div className="absolute top-6 left-6 flex items-center gap-3">
                    <div className="h-9 w-9 border border-[#C5A880] flex items-center justify-center">
                      <Icon size={16} className="text-[#C5A880]" />
                    </div>
                    <span className="overline">{area.label}</span>
                  </div>
                </div>

                <div className="p-8 lg:p-10">
                  <h3 className="font-display text-2xl lg:text-3xl font-medium leading-tight tracking-tight">
                    {area.title}
                  </h3>
                  <ul className="mt-8 space-y-3">
                    {area.items.map((it) => (
                      <li
                        key={it}
                        className="flex items-baseline gap-4 text-sm text-[#0A0A0A]/75 border-b border-[#0A0A0A]/8 pb-3"
                      >
                        <span className="text-[#1B3B36] text-[10px] font-mono">
                          ◆
                        </span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
